import { Box, Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadCatalog } from 'store/thunks/catalogThunks';

import CatalogDetails from './components/CatalogDetails';
import CatalogList from './components/CatalogList';
import CatalogSearchBar from './components/CatalogSearchBar';
import NewCatalogButton from './components/NewCatalogButton';
import { formatDrugName } from './utils/format';

const CatalogManagement = () => {
  const dispatch = useDispatch();
  const { drugs, loading, search } = useSelector(({ catalog }) => catalog);
  const [selectedCatalogId, setSelectedCatalogId] = useState(null);

  useEffect(() => {
    dispatch(loadCatalog());
  }, [dispatch]);

  // Flattened once from the drugName -> variants shape the API returns, so the
  // list can render one row per catalog variant (matches e-scribe-ui's Formulary
  // list). Kept unfiltered so the details panel can still resolve the selected
  // item even while a search term hides it from the list.
  const allRows = useMemo(
    () =>
      drugs
        .flatMap(({ drugName, variants }) =>
          (variants ?? []).map((variant) => ({ drugName, ...variant }))
        )
        .sort((left, right) =>
          formatDrugName(left.drugName).localeCompare(formatDrugName(right.drugName), undefined, {
            sensitivity: 'base',
          })
        ),
    [drugs]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return allRows;
    }

    return allRows.filter((row) => row.drugName?.toLowerCase().includes(term));
  }, [allRows, search]);

  const selectedItem = useMemo(
    () => allRows.find((row) => row.catalogId === selectedCatalogId) ?? null,
    [allRows, selectedCatalogId]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Grid
        container
        sx={{ px: 3, py: 2, flexShrink: 0 }}
        direction='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <Grid item sx={{ minWidth: 320 }}>
          <CatalogSearchBar />
        </Grid>
        <Grid item>
          <NewCatalogButton />
        </Grid>
      </Grid>

      <Grid container sx={{ px: 3, pb: 3, flex: 1, minHeight: 0 }} spacing={2}>
        <Grid item xs={12} sm={5} sx={{ height: '100%' }}>
          <CatalogList
            rows={rows}
            loading={loading}
            selectedCatalogId={selectedCatalogId}
            onSelect={setSelectedCatalogId}
          />
        </Grid>
        <Grid item xs={12} sm={7} sx={{ height: '100%' }}>
          <CatalogDetails item={selectedItem} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CatalogManagement;
